import { useResizeObserver } from "$root/hooks/useResizeObserver";
import { overrideClassName, toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { type ReactNode, type RefObject, memo, useRef, useState } from "react";
import { PageCtxProvider } from "./PageCtx";
import { PageHeaderOutlet } from "./PageHeader";
import type { PlatformLayoutOptions } from "./PlatformRouter/layout-options";

export type PageLayoutProps = {
	children?: ReactNode;
	title?: string;
	routeKey?: string;
	/**
	 * This property is used to hide both Sidebar and PageHeader
	 */
	hideSidebar?: boolean;
	mode?: PlatformLayoutOptions["mode"];
	maxPageHeight?: PlatformLayoutOptions["maxPageHeight"];
	padding?: PlatformLayoutOptions["padding"];
	minWidth?: PlatformLayoutOptions["minWidth"];
	maxWidth?: PlatformLayoutOptions["maxWidth"];
};

/** The resolved vertical fit published to page CSS as `data-page-fit`. */
type PageFit = "screen" | "grow";

export type DefaultModeProps = Omit<PageLayoutProps, "mode"> & {
	/** Ref + fit hook attached to the content element, so the layout layer can measure and publish it. */
	contentRef?: RefObject<HTMLDivElement | null>;
	fit?: PageFit;
};

const DefaultMode = ({ hideSidebar, children, padding = true, contentRef, fit }: DefaultModeProps) => {
	return (
		<PageCtxProvider>
			<div
				className={toClassName({
					[`grow min-h-0 flex flex-col bg-[color:var(--palette-N50)]`]: true,
				})}
			>
				<div
					ref={contentRef}
					data-page-fit={fit}
					className={overrideClassName("grow min-h-0 flex flex-col relative w-full", {
						"p-3.75": padding,
					})}
				>
					{!hideSidebar && <PageHeaderOutlet />}
					{children}
				</div>
			</div>
		</PageCtxProvider>
	);
};

/**
 * The `<main>` envelope: the single place that turns the layout contract (height cap, width floor/cap)
 * into the page box. `capped` toggles the viewport height cap; `data-page-fit` is published downward
 * by {@link DefaultMode} so page CSS reacts to this decision rather than re-measuring.
 */
function PageMain(props: DefaultModeProps & { capped: boolean }) {
	const { capped, minWidth, maxWidth, contentRef, ...defaultModeProps } = props;
	return (
		<main
			style={{
				minWidth,
				maxWidth,
				marginInline: maxWidth != null ? "auto" : undefined,
			}}
			className={toClassName({
				"min-h-dvh flex flex-col min-w-0": true,
				"max-h-dvh": capped,
			})}
		>
			<DefaultMode {...defaultModeProps} contentRef={contentRef} fit={capped ? "screen" : "grow"} />
		</main>
	);
}

/**
 * Responsive variant of {@link PageMain}: measures the content area's width and caps to the viewport
 * only while it is at least `capUntilWidth` wide; below that it lets the window scroll. The content
 * box (padding excluded) is measured so the threshold lines up exactly with a page's own
 * `@container` breakpoint on the same element.
 */
function ResponsivePageMain({ capUntilWidth, ...rest }: DefaultModeProps & { capUntilWidth: number }) {
	const contentRef = useRef<HTMLDivElement | null>(null);
	// Assume wide (capped) until measured — desktop-first, so a wide load never flashes a tall page.
	const [capped, setCapped] = useState(true);
	useResizeObserver({
		ref: contentRef as RefObject<HTMLDivElement>,
		box: "content-box",
		onResize: ({ width }) => {
			if (width === undefined) {
				return;
			}
			const next = width >= capUntilWidth;
			// Re-render only when the threshold is actually crossed, not on every pixel of resize.
			setCapped((prev) => (prev === next ? prev : next));
		},
	});
	return <PageMain {...rest} capped={capped} contentRef={contentRef} />;
}

function PageLayout(props: PageLayoutProps) {
	const { mode = "default", maxPageHeight = "auto" } = props;

	if (mode === "print") {
		return props.children;
	}

	const { mode: _mode, ...rest } = props;
	if (typeof maxPageHeight === "object") {
		return <ResponsivePageMain {...rest} capUntilWidth={maxPageHeight.capUntilWidth} />;
	}
	return <PageMain {...rest} capped={maxPageHeight === "screen"} />;
}

export default memo<PageLayoutProps>(PageLayout);
